/**
 * Hook compartido para tablas paginadas con cursor opaco del admin API.
 *
 * Encapsula:
 *   - estado `cursor` (efímero, no persiste en URL — paginar es transitorio)
 *   - `cursorStack` para navegación back-button intra-tabla
 *   - `goNext(nextCursor)` / `goPrev()` con la lógica del stack
 *   - `reset()` que se invoca cuando cambian los filtros (filtros nuevos
 *     deben empezar desde la primera página)
 *
 * No envuelve `useQuery` para mantenerlo agnóstico de TanStack y permitir
 * que cada page elija su queryKey / queryFn shape. El consumer hace:
 *
 *   const pager = useUrlPaginatedQuery();
 *   const query = useQuery({
 *     queryKey: ['x', { filtros, cursor: pager.cursor }],
 *     queryFn: () => fetchX({ ...filtros, cursor: pager.cursor }),
 *   });
 *   useEffect(() => pager.reset(), [filtroA, filtroB]);
 *   ...
 *   <button onClick={pager.goPrev} disabled={!pager.canGoPrev}>Prev</button>
 *   <button
 *     onClick={() => pager.goNext(query.data?.nextCursor)}
 *     disabled={!query.data?.nextCursor}
 *   >Next</button>
 */
import { useCallback, useState } from 'react';

export interface UrlPaginatedQueryApi {
  cursor: string | undefined;
  canGoPrev: boolean;
  goNext: (nextCursor: string | null | undefined) => void;
  goPrev: () => void;
  reset: () => void;
}

export function useUrlPaginatedQuery(): UrlPaginatedQueryApi {
  const [cursor, setCursor] = useState<string | undefined>(undefined);
  const [stack, setStack] = useState<string[]>([]);

  const goNext = useCallback((nextCursor: string | null | undefined) => {
    if (!nextCursor) return;
    setStack((prev) => [...prev, nextCursor]);
    setCursor(nextCursor);
  }, []);

  const goPrev = useCallback(() => {
    setStack((prev) => {
      if (prev.length === 0) return prev;
      const next = prev.slice(0, -1);
      // El cursor a aplicar es el último que quede en el stack (o `undefined`
      // si volvimos a la primera página).
      setCursor(next.length === 0 ? undefined : next[next.length - 1]);
      return next;
    });
  }, []);

  const reset = useCallback(() => {
    setCursor(undefined);
    setStack([]);
  }, []);

  return {
    cursor,
    canGoPrev: stack.length > 0,
    goNext,
    goPrev,
    reset,
  };
}
